import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  actionRequest,
  completeRequest,
  dialogueRequest,
  moduleRequest,
  verificationRequest,
} from 'core/services/product';
import { selectRegister } from 'core/selectors/product/register';
import { IHistoryItem, IRegisterReducer, next, queue } from 'core/slices/register';
import { IPlayerResponse, IResponseQueueItem } from 'types/action/player';
import { IVerificationRequestDAO } from 'types/domain/verification';
import { loadModule } from '../module/load';
import { IToken } from 'core/slices/session';
import { selectToken } from 'core/selectors/product/token';
import { IModuleAssign } from 'types/domain/module';
import { ICourseNodeDAO } from 'types/domain/course';
import { updatePageIds, waitForPageTransition } from './page';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { startModule } from '../module/start';

export function* handlePlayerResponse(playerResponse: IPlayerResponse) {
  const { queue, courseProgressId } = playerResponse;

  if (!queue.length) return;

  yield waitForPageTransition();

  const token: IToken = yield select(selectToken);

  yield all(
    queue.map(item =>
      processQueueItem(item, courseProgressId || token.courseProgressId, token.token || playerResponse.token),
    ),
  );

  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  if (!actionId) yield updatePageIds();

  const register: IRegisterReducer = yield select(selectRegister);
  if (register.isNext) yield put(yield call(next, { culprit: 'handlePlayerResponse' }));
}

function* processQueueItem(item: IResponseQueueItem, courseProgressId: number, token: string) {
  const register: IRegisterReducer = yield select(selectRegister);

  // if (
  //   item.verificationRequest &&
  //   !register.queue[courseProgressId]?.some(
  //     queueItem => queueItem.verificationRequestId === item.verificationRequest.id,
  //   )
  // )
  //   yield registerVer(item.verificationRequest, courseProgressId);
  if (
    item.module &&
    !register.queue[`${token}.${courseProgressId}`]?.some(
      queueItem => queueItem.sessionAttemptId === item.module.sessionAttemptId,
    )
  )
    yield prepareModule(item.module, courseProgressId, token);
  if (
    item.dialogue &&
    !register.queue[`${token}.${courseProgressId}`]?.some(
      queueItem => queueItem.courseNodeId === item.dialogue.courseNode.id,
    )
  )
    yield registerDialogue(item.dialogue.courseNode, courseProgressId, token);
}

function* registerDialogue(cn: ICourseNodeDAO, courseProgressId: number, token: string) {
  yield put(yield call(queue, { courseNodeId: cn.id, courseProgressId, token } as IHistoryItem));
}

function* registerVer(vr: IVerificationRequestDAO, courseProgressId: number) {
  yield put(yield call(queue, { verificationRequestId: vr.id, courseProgressId } as IHistoryItem));
}

function* prepareModule(module: IModuleAssign, courseProgressId: number, token: string) {
  yield put(
    yield call(queue, {
      moduleId: module.id,
      courseProgressId,
      sessionAttemptId: module.sessionAttemptId,
      token,
    } as IHistoryItem),
  );

  let loading = yield loadModule(module.id);

  while (loading) {
    const action = yield take(moduleRequest.fulfilled);
    loading = action.payload.id !== module.id;
  }
  const feed: IActionFeed = yield select(selectActionFeed);
  const current = feed.feed[feed.index];
  if (current && current.moduleId === module.id)
    yield call(startModule, module.sessionAttemptId, module.id, current.actionId);
}

function* playerResponseSaga({ payload, meta }) {
  yield handlePlayerResponse(payload);
}

function* watchResponses() {
  yield takeEvery(
    [actionRequest.fulfilled, verificationRequest.fulfilled, dialogueRequest.fulfilled, completeRequest.fulfilled],
    playerResponseSaga,
  );
}

export function* responseSaga() {
  yield all([fork(watchResponses)]);
}
